// ADV-264 — the Finanzierungsdetails / Leasingdetails layer (Figma "KONTO | Layer | …details",
// desktop 64254:14113 / 14235, mobile sheet 64254:14173 / 14293) on the shared md3 Modal (focus
// trap, Esc, bottom sheet on phones).
//
// Figures: the calculation the checkout saved with the order (contractTermsOf) — the fe-financing
// config behind the VDP's and the checkout's own details layer (VdpModules FinanceDetailsModal),
// as it was when the customer ordered, listed with that layer's row rules. The rate is the
// contract's own (a financed Rundum-Sorglos-Paket included), as on the VDP and the checkout's
// payment step, so the example adds up — (n − 1) × rate + Schlussrate = Gesamtbetrag. The
// Anschluss-Garantie is a separate product: the order card adds it to its Gesamtrate (user's call,
// 2026-10-21; the checkout's last-step layer puts it into the rate and its sums stop adding up).
// One deliberate difference: a Rundum-Sorglos-Paket financed with the rates is already part of
// the rates, so the Gesamtbelastung does not add it a second time. A row the calculation has no
// figure for shows
// "—", as in Figma. Without a matching calculation (none saved, or sales changed the terms in
// Odoo since) only the order's own figures are listed.
import * as React from 'react';
import { Modal } from '@bytebrand/fe-ui-core-autobahn/ui';
import { carTitle, companyName, contractTermsOf, euro, hasAmount, hasValue, kilometers, lenderOf, percent, } from './orderData';
import closeIcon from './icons/dialog-close.svg';
const ns = (key) => `MyOrderPage:orders.dlg.${key}`;
const months = (count, t) => t('MyOrderPage:orders.months', { count });
/** Rundum-Sorglos-Paket as a separate payment: 0 € when it is financed with the rates. */
const packageOnce = (terms) => {
    if (!hasAmount(terms.addServicesCostRaw))
        return undefined;
    return terms.withAddServices ? 0 : terms.addServicesCostRaw;
};
const financingRows = (terms, t) => {
    const dash = t(ns('notAvailable'));
    const balloon = hasAmount(terms.finalInstallment) ? terms.finalInstallment : undefined;
    const pack = packageOnce(terms);
    const total = hasAmount(terms.totalFinancingAmount) ? terms.down + terms.totalFinancingAmount + (pack || 0) : undefined;
    return [
        { label: t(ns('vehiclePrice')), value: euro(terms.price) },
        { label: t(ns('term')), value: months(terms.term, t) },
        { label: t(ns('downPayment')), value: euro(terms.down) },
        // with a Schlussrate the last of the term's payments is the Schlussrate itself
        { label: t(ns('rates'), { count: balloon ? terms.term - 1 : terms.term }), value: euro(terms.monthly) },
        { label: t(ns('finalInstallment')), value: balloon ? euro(balloon) : dash },
        { label: t(ns('packageFinancing')), value: pack !== undefined ? euro(pack) : dash },
        { label: t(ns('nominalRate')), value: hasAmount(terms.nominalRate) ? percent(terms.nominalRate) : dash },
        { label: t(ns('effectiveRate')), value: hasAmount(terms.apr) ? percent(terms.apr) : dash },
        { label: t(ns('netLoanAmount')), value: hasValue(terms.netLoanAmount) ? euro(terms.netLoanAmount) : dash },
        { label: t(ns('totalRepayment')), value: hasAmount(terms.totalFinancingAmount) ? euro(terms.totalFinancingAmount) : dash },
        {
            label: t(ns(pack !== undefined ? 'totalCostWithPackage' : 'totalCost')),
            value: total !== undefined ? euro(total) : dash,
            total: true,
        },
        { label: t(ns('listPriceFinancing')), value: hasAmount(terms.listPrice) ? euro(terms.listPrice) : dash },
    ];
};
const leasingRows = (terms, t) => {
    const dash = t(ns('notAvailable'));
    const pack = packageOnce(terms);
    return [
        { label: t(ns('acquisitionPrice')), value: euro(terms.price) },
        { label: t(ns('term')), value: months(terms.term, t) },
        { label: t(ns('annualMileage')), value: hasAmount(terms.annualMileage) ? kilometers(terms.annualMileage) : dash },
        { label: t(ns('specialPayment')), value: euro(terms.down) },
        { label: t(ns('monthlyRates'), { count: terms.term }), value: euro(terms.monthly) },
        // Restwertleasing has no payment due at the end; the residual value is the purchase price below
        { label: t(ns('finalPaymentDue')), value: euro(0) },
        { label: t(ns('residualValue')), value: hasAmount(terms.finalInstallment) ? euro(terms.finalInstallment) : dash },
        { label: t(ns('packageLeasing')), value: pack !== undefined ? euro(pack) : dash },
        { label: t(ns('nominalRate')), value: hasAmount(terms.nominalRate) ? percent(terms.nominalRate) : dash },
        { label: t(ns('effectiveRate')), value: hasAmount(terms.apr) ? percent(terms.apr) : dash },
        {
            label: t(ns('leasingRatesSum'), { count: terms.term, rate: euro(terms.monthly) }),
            value: euro(terms.term * terms.monthly),
            total: true,
        },
        { label: t(ns('listPriceLeasing')), value: hasAmount(terms.listPrice) ? euro(terms.listPrice) : dash },
    ];
};
/** Without a matching saved calculation: the order's own figures only. */
const orderRows = (lead, kind, t) => {
    const term = lead.paybackPeriod;
    const rate = lead.monthlyInstallment;
    const price = lead.currentSalesPrice;
    const rows = [];
    if (kind === 'financing') {
        if (hasAmount(price))
            rows.push({ label: t(ns('vehiclePrice')), value: euro(price) });
        if (hasAmount(term))
            rows.push({ label: t(ns('term')), value: months(term, t) });
        if (hasValue(lead.deposit))
            rows.push({ label: t(ns('downPayment')), value: euro(lead.deposit) });
        return rows;
    }
    if (hasAmount(price))
        rows.push({ label: t(ns('acquisitionPrice')), value: euro(price) });
    if (hasAmount(term))
        rows.push({ label: t(ns('term')), value: months(term, t) });
    if (hasValue(lead.deposit))
        rows.push({ label: t(ns('specialPayment')), value: euro(lead.deposit) });
    if (hasAmount(term) && hasAmount(rate)) {
        rows.push({ label: t(ns('monthlyRates'), { count: term }), value: euro(rate) });
        rows.push({ label: t(ns('leasingRatesSum'), { count: term, rate: euro(rate) }), value: euro(term * rate), total: true });
    }
    return rows;
};
const OrderDetailsModal = ({ lead, kind, contract, t, onClose }) => {
    const { car } = lead;
    const terms = contractTermsOf(lead, kind, contract);
    let rows;
    if (!terms)
        rows = orderRows(lead, kind, t);
    else
        rows = kind === 'financing' ? financingRows(terms, t) : leasingRows(terms, t);
    const lender = (terms && terms.lender) || lenderOf(car, kind, t);
    let legal;
    let foot = '';
    if (kind === 'financing') {
        legal = [1, 2, 3, 4, 5].map((n) => t(ns(`legalFinancing${n}`)));
        // ³ belongs to the Listenpreis row, which only the saved calculation lists
        if (terms)
            foot = t(ns('legalFinancingFoot'));
    }
    else {
        legal = [t(ns('legalLeasing1'))];
        // ¹ belongs to the Anschaffungspreis row; the lender is named without its address.
        if (terms || hasAmount(lead.currentSalesPrice))
            legal.push(t(ns('legalLeasing2'), { lender: companyName(lender) }));
        if (terms)
            legal.push(t(ns('legalLeasing3')));
        foot = t(ns('legalLeasingFoot'));
    }
    const subtitle = carTitle(car);
    return (React.createElement(Modal, { open: true, onClose: onClose, className: "ao-dlg", closeIcon: React.createElement("img", { src: closeIcon, alt: "", width: 22, height: 22 }), title: (React.createElement(React.Fragment, null,
            React.createElement("span", { className: "ao-dlg-title" }, t(ns(kind === 'financing' ? 'financingTitle' : 'leasingTitle'))),
            subtitle ? React.createElement("span", { className: "ao-dlg-sub" }, subtitle) : null)) },
        React.createElement("div", { className: "ao-dlg-example" },
            React.createElement("span", { className: "ao-dlg-example-title" }, t(ns('example'))),
            React.createElement("span", { className: "ao-dlg-example-note" }, t(ns('vatNote')))),
        rows.map((row) => (React.createElement("div", { key: row.label, className: row.total ? 'ao-dlg-row ao-dlg-row--total' : 'ao-dlg-row' },
            React.createElement("span", { className: "ao-dlg-row-label" }, row.label),
            React.createElement("span", { className: "ao-dlg-row-value" }, row.value)))),
        React.createElement("div", { className: "ao-dlg-lender-wrap" },
            React.createElement("div", { className: "ao-dlg-lender" },
                React.createElement("span", { className: "ao-dlg-lender-label" }, t(ns(kind === 'financing' ? 'lenderFinancing' : 'lenderLeasing'))),
                React.createElement("span", { className: "ao-dlg-lender-name" }, lender))),
        React.createElement("div", { className: "ao-dlg-legal" },
            React.createElement("span", { className: "ao-dlg-legal-title" }, t(ns('legalTitle'))),
            legal.map((line) => React.createElement("span", { key: line, className: "ao-dlg-legal-item" }, `•  ${line}`)),
            foot ? React.createElement("span", { className: "ao-dlg-legal-foot" }, foot) : null)));
};
export default OrderDetailsModal;
